<template>
	<tr>
		<td class="text-truncate" v-show="isColumnEnabled[0]">
			{{ sitemapPathname }}
		</td>
		<td class="text-center" v-show="isColumnEnabled[1]">
			{{ result.type }}
		</td>
		<td :class="['text-end', statusClass]" v-show="isColumnEnabled[2]">
			{{ result.status }}
		</td>
		<td :class="['text-end', durationClass]" v-show="isColumnEnabled[3]">
			{{ result.durationMS / 1000 }}
		</td>
		<td class="text-truncate" v-show="isColumnEnabled[4]">
			<a
				class="link-secondary"
				:href="result.url"
				target="_blank"
				rel="noopener noreferrer"
				>{{ resourceUrl }}</a
			>
		</td>

		<!-- sources -->
		<td
			v-show="isColumnEnabled[5]"
			:style="{
				'max-width': '0',
				'white-space': 'nowrap',
			}"
		>
			<div class="d-flex gap-1 align-items-center">
				<!-- first source -->
				<span class="text-truncate">
					<a
						class="link-secondary"
						:href="firstSource.url"
						target="_blank"
						rel="noopener noreferrer"
						>{{ urlToDisplayableUrl(firstSource.url) }}
					</a>
				</span>
				<!-- dropdown with all sources -->
				<div class="dropdown" v-if="extraSourcesCount > 0">
					<button
						class="btn btn-sm btn-outline-secondary"
						type="button"
						data-bs-toggle="dropdown"
						:style="{ padding: '0.05rem 0.2rem' }"
					>
						{{ "+" + extraSourcesCount }}
					</button>
					<ul class="dropdown-menu extra-items-dropdown">
						<li
							v-for="source in result.sources.slice(1)"
							class="dropdown-item-wrapper"
							:key="source.url"
						>
							<a
								class="
									dropdown-item-text
									link-secondary
									text-truncate
								"
								target="_blank"
								:href="source.url"
								rel="noopener noreferrer"
								>{{ urlToDisplayableUrl(source.url) }}
							</a>
						</li>
					</ul>
				</div>
			</div>
		</td>

		<!-- anchors -->
		<td
			v-show="isColumnEnabled[6]"
			:style="{
				'max-width': '0',
				'white-space': 'nowrap',
			}"
		>
			<div v-if="hasAnchors" class="d-flex gap-1 align-items-center">
				<!-- first anchor -->
				<ResourceAnchor
					:resourceType="result.type"
					:source="firstSource"
				/>

				<!-- dropdown with other anchors -->
				<div class="dropdown" v-if="extraSourcesCount > 0">
					<button
						class="btn btn-sm btn-outline-secondary"
						type="button"
						data-bs-toggle="dropdown"
						:style="{ padding: '0.05rem 0.2rem' }"
					>
						{{ "+" + extraSourcesCount }}
					</button>

					<ul class="dropdown-menu extra-items-dropdown">
						<li
							v-for="source in result.sources.slice(1)"
							class="dropdown-item-wrapper"
							:key="source.url"
							@click.stop=""
						>
							<ResourceAnchor
								class="dropdown-item-text text-decoration-none"
								:resourceType="result.type"
								:source="source"
							/>
						</li>
					</ul>
				</div>
			</div>
		</td>

		<td class="text-center">
			<button
				v-if="isError"
				class="btn btn-sm btn-outline-secondary"
				@click="hideError(result.url)"
			>
				Hide
			</button>
		</td>
	</tr>
</template>

<script lang="ts">
import { hiddenErrorsService } from "@/services/hiddenErrorsService";
import { ResourceResult } from "@/services/resourceWatcherService";
import { getPathname, isErrorStatusCode } from "@/utils";
import { ResourceType } from "@/utils/watcher";
import { computed, defineComponent, toRefs } from "vue";
import ResourceAnchor from "./ResourceAnchor.vue";

export default defineComponent({
	components: { ResourceAnchor },
	props: {
		result: {
			type: Object as () => ResourceResult,
			required: true,
		},
		showFullDomain: {
			type: Boolean,
			default: false,
		},
		columnsBitmask: {
			type: Array as () => boolean[],
			default: null,
		},
	},
	setup(props) {
		const { result, showFullDomain, columnsBitmask } = toRefs(props);

		const urlToDisplayableUrl = (url: string) =>
			showFullDomain.value ? url : getPathname(url);

		const sitemapPathname = computed(() =>
			showFullDomain.value
				? result.value.sitemap
				: getPathname(result.value.sitemap)
		);
		const resourceUrl = computed(() =>
			urlToDisplayableUrl(result.value.url)
		);

		const firstSource = computed(() => result.value.sources[0]);
		const extraSourcesCount = computed(
			() => result.value.sources.length - 1
		);

		const isError = isErrorStatusCode(result.value.status);
		const statusClass = isError ? "table-danger" : "table-success";

		let durationClass = "";
		if (1000 <= result.value.durationMS && result.value.durationMS < 1500) {
			durationClass = "table-warning";
		} else if (1500 <= result.value.durationMS) {
			durationClass = "table-danger";
		}

		const { hiddenErrors, hideError } = hiddenErrorsService;
		const isHidden = computed(
			() => !!hiddenErrors.value.has(result.value.url)
		);

		const hasAnchors = computed(() => !!result.value.sources[0].anchor);

		return {
			sitemapPathname,
			resourceUrl,

			firstSource,
			extraSourcesCount,

			isColumnEnabled: columnsBitmask,
			isError,
			statusClass,
			durationClass,

			urlToDisplayableUrl,

			isHidden,
			hideError,

			hasAnchors,

			ResourceType,
		};
	},
});
</script>

<style scoped>
.extra-items-dropdown {
	max-height: 400px;
	overflow-y: auto;
	max-width: 400px;
}

.dropdown-item-wrapper:hover {
	background: #e9ecef;
}
</style>
